/**
 * Smoke Drive → Printing Toolkit mixed print queue (General print path).
 * Stages PDF / image / text / HTML / docx placeholder the same way as GeneralPrintPanel.
 */

import { putPrintQueueBlob } from '../services/printQueueStore';
import {
    getDefaultQzSettings,
    loadQzPrintSettings,
} from '../services/qzPrintService';
import {
    loadPrintingToolkitSession,
} from '../services/printingToolkitSessionStore';
import {
    buildGeneralPrintHtmlFromTextFile,
    classifyGeneralPrintFile,
    convertImageFileToPdfBlob,
    describeUnsupportedGeneralPrintFile,
    GENERAL_PRINT_MAX_BYTES,
    inspectPdfBlob,
} from './generalPrintFileSupport';
import { getGeneralPrintSize } from './generalPrintSizes';
import { fetchDriveStorageUrl } from './operatorDriveShareDownload';
import { driveDownloadFileName } from './operatorDriveFileName';

function fileNameOf(item) {
    return driveDownloadFileName(item);
}

/**
 * Pseudo-File for classification from drive metadata (no bytes yet).
 * @param {{ name?: string, originalFileName?: string, contentType?: string }} item
 */
export function driveItemAsClassifiableFile(item) {
    return {
        name: fileNameOf(item),
        type: String(item?.contentType || '').trim(),
        size: Number(item?.sizeBytes) || 1,
    };
}

/**
 * @param {{ name?: string, originalFileName?: string, contentType?: string, type?: string }} item
 * @returns {boolean}
 */
export function isOperatorDriveItemPrintable(item) {
    if (!item || item.type === 'folder') return false;
    const kind = classifyGeneralPrintFile(driveItemAsClassifiableFile(item));
    return kind !== 'unsupported';
}

async function downloadDriveFileAsFile(item, resolveDownloadUrl) {
    let url = String(item.downloadUrl || '').trim();
    if (!url && typeof resolveDownloadUrl === 'function') {
        url = String((await resolveDownloadUrl(item)) || '').trim();
    }
    if (!url) throw new Error('No download link available for this file.');

    const response = await fetchDriveStorageUrl(url, 'the file for printing');
    const blob = await response.blob();
    if (!blob?.size) throw new Error('The file is empty.');
    if (blob.size > GENERAL_PRINT_MAX_BYTES) {
        throw new Error(
            'This file is larger than the 50 MB General print safety limit. Download it and print from Printing Toolkit after splitting, or use a smaller export.'
        );
    }
    const fileName = fileNameOf(item);
    return new File([blob], fileName, {
        type: item.contentType || blob.type || '',
    });
}

/**
 * Queue a Smoke Drive file into the workstation mixed print queue (QZ / Printing Toolkit).
 *
 * @param {object} item
 * @param {{
 *   enqueueGeneralJob: Function,
 *   sizeKey?: string,
 *   copies?: number,
 *   pageRanges?: string,
 *   resolveDownloadUrl?: (item: object) => Promise<string>,
 * }} opts
 * @returns {Promise<{ bundleId: string, kind: string, sizeKey: string, label: string, message: string }>}
 */
export async function enqueueOperatorDriveItemToPrintQueue(item, opts = {}) {
    if (!item || item.type === 'folder') {
        throw new Error('Folders cannot be printed.');
    }
    if (typeof opts.enqueueGeneralJob !== 'function') {
        throw new Error('Print queue is not available.');
    }
    if (!isOperatorDriveItemPrintable(item)) {
        throw new Error(
            describeUnsupportedGeneralPrintFile(driveItemAsClassifiableFile(item))
        );
    }
    if (Number(item.sizeBytes) > GENERAL_PRINT_MAX_BYTES) {
        throw new Error(
            'This file is larger than the 50 MB General print safety limit. Download it and print locally, or use a smaller export.'
        );
    }

    const session = loadPrintingToolkitSession();
    const sizeKey = opts.sizeKey || session.generalSizeKey || 'A4';
    const copies = Math.max(1, Number(opts.copies) || Number(session.generalCopies) || 1);
    const pageRanges =
        opts.pageRanges != null
            ? String(opts.pageRanges)
            : String(session.generalPageRanges || '');
    const size = getGeneralPrintSize(sizeKey);
    const qzSettings = loadQzPrintSettings();
    const routing =
        qzSettings[size.profileKey] || getDefaultQzSettings(size.profileKey);

    const file = await downloadDriveFileAsFile(item, opts.resolveDownloadUrl);
    const kind = classifyGeneralPrintFile(file);

    const enqueueHtml = (html, label) => {
        const bundleId = opts.enqueueGeneralJob({
            kind: 'general-html',
            profileKey: size.profileKey,
            sizeKey: size.key,
            label,
            recipe: { html, copies, sizeKey: size.key },
            routing,
        });
        return {
            bundleId,
            kind: 'general-html',
            sizeKey: size.key,
            label,
            message: `${label} added to the mixed print queue.`,
        };
    };

    const enqueuePdfBlob = async (blob, fileName, sourceLabel) => {
        const meta = await inspectPdfBlob(blob);
        const blobRef = `general-file-${globalThis.crypto?.randomUUID?.() || Date.now()}`;
        await putPrintQueueBlob(blobRef, blob, {
            temporary: false,
            name: fileName,
            sizeKey: size.key,
        });
        const label = `${size.label} ${sourceLabel} · ${fileName}`;
        const bundleId = opts.enqueueGeneralJob({
            kind: 'general-pdf',
            profileKey: size.profileKey,
            sizeKey: size.key,
            label,
            recipe: {
                blobRef,
                fileName,
                pageCount: meta.pageCount,
                copies,
                pageRanges: sourceLabel === 'PDF' ? pageRanges : '',
                sizeKey: size.key,
            },
            routing,
        });
        return {
            bundleId,
            kind: 'general-pdf',
            sizeKey: size.key,
            label,
            message: `${fileName} (${meta.pageCount} page${
                meta.pageCount === 1 ? '' : 's'
            }) added on ${size.label}. Open Print queue to run.`,
        };
    };

    if (kind === 'docx') {
        const blobRef = `general-file-${globalThis.crypto?.randomUUID?.() || Date.now()}`;
        await putPrintQueueBlob(blobRef, file, {
            temporary: false,
            name: file.name,
            sizeKey: size.key,
        });
        const label = `${size.label} Word · ${file.name}`;
        const bundleId = opts.enqueueGeneralJob({
            kind: 'general-docx',
            profileKey: size.profileKey,
            sizeKey: size.key,
            label,
            recipe: {
                blobRef,
                fileName: file.name,
                copies,
                sizeKey: size.key,
            },
            routing,
        });
        return {
            bundleId,
            kind: 'general-docx',
            sizeKey: size.key,
            label,
            message: `${file.name} is in the queue on ${size.label}. Export as PDF to print that slot; the rest of the line still runs.`,
        };
    }

    if (kind === 'pdf') {
        return enqueuePdfBlob(file, file.name, 'PDF');
    }
    if (kind === 'image') {
        const pdfBlob = await convertImageFileToPdfBlob(file, size);
        const pdfName = String(file.name || 'image').replace(/\.[^.]+$/, '.pdf');
        return enqueuePdfBlob(pdfBlob, pdfName, 'Image');
    }
    if (kind === 'text' || kind === 'html') {
        const html = await buildGeneralPrintHtmlFromTextFile(file, size);
        return enqueueHtml(html, `${size.label} file · ${file.name}`);
    }

    throw new Error(
        describeUnsupportedGeneralPrintFile(driveItemAsClassifiableFile(item))
    );
}

/** @deprecated Use enqueueOperatorDriveItemToPrintQueue */
export async function printOperatorDriveItem(item, opts = {}) {
    return enqueueOperatorDriveItemToPrintQueue(item, opts);
}
